/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useCallback, useState } from "react"
import { useTable, useFilters, useSortBy, usePagination } from "react-table"
import { useTranslation } from "react-i18next"
import { Input, Select } from "components/form"
import { TransportContext } from "stores/ManagementTransportStore"
import { Card, FlexView, Link, LoadingOverlay } from "components/common"
import { TableWrapper } from "components/common/Table"

export const SelectColumnFilter = ({ column: { filterValue, setFilter, preFilteredRows, id, filteredRows } }) => {
  const [selectedValue, setSelectedValue] = useState('')

  const onSelect = (selectedList) => {
    setSelectedValue(selectedList)
    setFilter(selectedList)
  }

  const options = React.useMemo(() => {
    const options = new Set()
    preFilteredRows.forEach((row) => {
      options.add(row.values[`${id}`])
    })
    return [...options.values()]
  }, [id, preFilteredRows])

  const values = [{ value: '', label: 'Todos' }].concat(options.map((item) => { return { value: item, label: item } }))

  return (
    <Select
      width="auto"
      options={values}
      onChange={(e) => onSelect(e)}
      value={selectedValue}
      placeholder={selectedValue || ''}
      closeOnSelect={true}
      height={'25px'}
      alignItems= 'center'
    />
  )
}

const FilteredTable = ({ columns, data, ...rest }) => {
  const { t } = useTranslation()
  const { loading } = useContext(TransportContext)
  const DefaultColumnFilter = ({ column: { filterValue, setFilter } }) => {
    const onChange = useCallback(
      (e) => {
        setFilter(e.target.value || undefined)
      },
      [setFilter]
    )

    return (
      <Input
        value={filterValue || ""}
        onChange={onChange}
        margin="0px"
        width="100%"
      />
    )
  }
  const tableOptions = {
    defaultColumn: {
      Filter: DefaultColumnFilter
    },
    autoResetPage: false,
    autoResetFilters: false,
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    rows,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: data,
      ...tableOptions,
    },
    useFilters,
    useSortBy,
    usePagination
  )

  return <Card alignSelf="stretch" padding="0px">
    {loading ? (
      <LoadingOverlay visible={loading} />
    ) : (
      <TableWrapper elevation="none" width="100%" fontSize="13px" {...rest}>
        <div className="table-wrap">
          <table {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th
                      {...column.getHeaderProps(column.customHeaderProps)}
                      className="sortable"
                    >
                      <div {...column.getSortByToggleProps({ title: "" })}>
                        {column.render("Header")}
                        <span>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <svg
                                width="13"
                                height="7"
                                viewBox="0 0 13 7"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                transform="rotate(180)"
                              >
                                <path
                                  d="M0 6.5L6.5 6.5L13 6.5L6.5 0L0 6.5Z"
                                  fill="#777777"
                                />
                              </svg>
                            ) : (
                              <svg
                                width="13"
                                height="7"
                                viewBox="0 0 13 7"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M0 6.5L6.5 6.5L13 6.5L6.5 0L0 6.5Z"
                                  fill="#777777"
                                />
                              </svg>
                            )
                          ) : (
                            ""
                          )}
                        </span>
                      </div>
                      <div>
                        {column.canFilter ? column.render("Filter") : null}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row)
                return (
                  <tr
                    {...row.getRowProps({
                      style: { cursor: "pointer" },
                    })}
                  >
                    {row.cells.map((cell) => {
                      return (
                        <td
                          {...cell.getCellProps(
                            cell.column.customCellProps
                          )}
                        >
                          {cell.render("Cell")}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <FlexView
            flexDirection="row"
            justifyContent="flex-start"
            alignItems="center"
            flex="1"
          >
            <Input
              inline
              type="number"
              label={t("goToPage")}
              fontSize="13px"
              margin="0px"
              padding="8px 4px"
              defaultValue={pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value
                  ? Number(e.target.value) - 1
                  : 0
                gotoPage(page)
              }}
              width="180px"
            />
          </FlexView>
          <FlexView
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            fontSize="13px"
          >
            <Link
              noDecoration
              color="gray"
              fontSize="big"
              fontWeight="bold"
              padding="4px"
              margin="0px 4px"
              onClick={() => gotoPage(0)}
              disabled={!canPreviousPage}
            >
              {"«"}
            </Link>{" "}
            <Link
              noDecoration
              color="gray"
              fontSize="big"
              fontWeight="bold"
              padding="4px"
              margin="0px 4px"
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
            >
              {"‹"}
            </Link>{" "}
            <FlexView
              flexDirection="row"
              margin="0px 8px"
              fontWeight="bold"
            >
              {pageIndex + 1} {t("of")} {pageOptions.length}
            </FlexView>
            <Link
              noDecoration
              color="gray"
              fontSize="big"
              fontWeight="bold"
              padding="4px"
              margin="0px 4px"
              onClick={() => nextPage()}
              disabled={!canNextPage}
            >
              {"›"}
            </Link>{" "}
            <Link
              noDecoration
              color="gray"
              fontSize="big"
              fontWeight="bold"
              padding="4px"
              margin="0px 4px"
              onClick={() => gotoPage(pageCount - 1)}
              disabled={!canNextPage}
            >
              {"»"}
            </Link>{" "}
          </FlexView>
          <FlexView
            flex="1"
            flexDirection="row"
            justifyContent="flex-end"
            alignItems="center"
          >
            <FlexView
              flexDirection="row"
              alignItems="center"
              fontSize="small"
              borderRadius="component"
              backgroundColor="whitesmoke"
              padding="8px"
              margin="0px 16px"
            >
              <FlexView margin="0px 8px 0px 0px" color="secondary">
                {rows.length}
              </FlexView>
              <span>{t("items")}</span>
            </FlexView>
            <Select
              margin="0px"
              value={pageSize}
              options={[5, 10, 20, 30, 40, 50].map((pageSize) => ({
                value: pageSize,
                label: `${t("show")} ${pageSize}`,
              }))}
              onChange={(value) => {
                setPageSize(Number(value))
              }}
            />
          </FlexView>
        </div>
      </TableWrapper>
    )}
  </Card>
}

export default FilteredTable
